import { useId, useState } from 'react';
import type { AdminWarehouse } from '../types/inventory.js';
import type { CreateWarehouseInput } from '../api/inventory-api.js';

export interface WarehouseFormProps {
  initial?: AdminWarehouse;
  onSubmit: (input: CreateWarehouseInput) => void;
  onCancel: () => void;
  isSubmitting: boolean;
}

export function WarehouseForm({ initial, onSubmit, onCancel, isSubmitting }: WarehouseFormProps) {
  const [code, setCode] = useState(initial?.code ?? '');
  const [name, setName] = useState(initial?.name ?? '');
  const [city, setCity] = useState(initial?.city ?? '');
  const [state, setState] = useState(initial?.state ?? '');
  const [error, setError] = useState<string | null>(null);
  const codeId = useId();
  const nameId = useId();

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!code.trim() || !name.trim()) {
      setError('Code and name are required.');
      return;
    }
    setError(null);
    onSubmit({ code: code.trim(), name: name.trim(), city: city.trim() || null, state: state.trim() || null });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3 rounded-xl border border-gray-100 bg-gray-50 p-4">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor={codeId} className="mb-1 block text-xs font-medium text-gray-600">
            Code
          </label>
          <input
            id={codeId}
            value={code}
            onChange={(event) => setCode(event.target.value)}
            disabled={Boolean(initial)}
            className="focus-visible:ring-brand w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm uppercase focus-visible:outline-none focus-visible:ring-2 disabled:bg-gray-100"
          />
        </div>
        <div>
          <label htmlFor={nameId} className="mb-1 block text-xs font-medium text-gray-600">
            Name
          </label>
          <input
            id={nameId}
            value={name}
            onChange={(event) => setName(event.target.value)}
            className="focus-visible:ring-brand w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">City</label>
          <input
            value={city}
            onChange={(event) => setCity(event.target.value)}
            className="focus-visible:ring-brand w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">State</label>
          <input
            value={state}
            onChange={(event) => setState(event.target.value)}
            className="focus-visible:ring-brand w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2"
          />
        </div>
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={isSubmitting}
          className="bg-brand text-brand-foreground rounded-full px-4 py-1.5 text-sm font-semibold disabled:opacity-60"
        >
          {isSubmitting ? 'Saving…' : 'Save warehouse'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-full border border-gray-200 px-4 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
